// ── RecentContractsSection — 최근계약현황 직원별 설정 관리 ────────────────────
// - RECENT_CONTRACTS_FEATURE 배정된 직원이 자동으로 목록에 표시
// - 직원마다 개별 개월수 + 표시 상품 설정 가능
// - 저장 버튼은 직원별로 독립 (한 명씩 저장)

import React, { useState } from 'react';
import AdminColHeader from '../../../components/AdminColHeader';
import { cn } from '../../../lib/utils';

export function RecentContractsSection({
  rcConfig,           // { ticket_types, assigned_users }
  rcConfigLoading,
  rcUserSettings,     // { [userId]: { months, ticketTypeIds: Set } }
  rcSavingUserId,     // 현재 저장 중인 직원 ID
  setRcUserMonths,    // (userId, months) => void
  toggleRcUserTicketType, // (userId, ttId) => void
  saveRcUserSettings, // (userId) => void
  saving,
}) {
  // 상품 선택 UI 펼침/접힘 상태 { [userId]: boolean }
  const [expandedUser, setExpandedUser] = useState(null);

  if (rcConfigLoading) {
    return (
      <section className="bg-white dark:bg-pine-900 border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="px-3.5 py-3 text-xs text-[var(--text-muted)]">불러오는 중...</div>
      </section>
    );
  }

  // 카테고리별 상품 그룹화 (체크박스 표시용)
  const grouped = {};
  (rcConfig?.ticket_types || []).forEach((tt) => {
    const cat = tt.category || '기타';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(tt);
  });
  const categories = Object.keys(grouped).sort();

  const assignedUsers = rcConfig?.assigned_users || [];

  return (
    <section className="bg-white dark:bg-pine-900 border border-[var(--border)] rounded-xl overflow-hidden">
      {/* ── 헤더 ── */}
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-[var(--border)]">
        <span className="text-[13px] font-extrabold text-[var(--text)]">
          <AdminColHeader label="최근계약현황 관리" db="tm_recent_contracts_settings (user_id, months, ticket_type_ids)" />
        </span>
      </div>

      <div className="p-3.5 flex flex-col gap-3">

        {/* ── 배정 없음 안내 ── */}
        {assignedUsers.length === 0 && (
          <div className="text-[11px] text-[var(--text-muted)] italic">
            배정된 직원이 없습니다. (업무/기능 배정 → N개월 내 계약현황)
          </div>
        )}

        {/* ── 직원별 설정 카드 ── */}
        {assignedUsers.map((user) => {
          const uSettings     = rcUserSettings[user.id] || { months: 3, ticketTypeIds: new Set() };
          const isSaving      = rcSavingUserId === user.id;
          const isExpanded    = expandedUser === user.id;
          const selectedCount = uSettings.ticketTypeIds?.size ?? 0;

          return (
            <div key={user.id}
              className="border border-[var(--border)] rounded-lg overflow-hidden bg-[var(--bg-subtle)]">

              {/* 직원 이름 + 개월수 + 저장 버튼 (항상 표시) */}
              <div className="flex items-center gap-2 px-3 py-2">
                {/* 이름·부서 */}
                <span className="text-[12px] font-bold text-[var(--text)] min-w-[60px]">
                  {user.name}
                </span>
                {user.department && (
                  <span className="text-[10px] text-[var(--text-muted)]">· {user.department}</span>
                )}

                {/* 개월수 입력 */}
                <input
                  type="number" min="1" max="120"
                  value={uSettings.months}
                  onChange={(e) => setRcUserMonths(user.id, e.target.value)}
                  className="ml-auto w-14 border border-accent-500 rounded-md px-1.5 py-[3px] text-xs font-bold text-[var(--text)] outline-none text-center bg-white dark:bg-pine-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-[11px] text-[var(--text-muted)]">개월</span>

                {/* 상품 선택 토글 버튼 */}
                <button
                  type="button"
                  onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                  className={cn(
                    'px-2.5 py-[4px] rounded-lg text-[11px] font-semibold border transition-colors',
                    selectedCount > 0
                      ? 'bg-accent-50 text-accent-600 border-accent-300 dark:bg-pine-800'
                      : 'bg-[var(--bg-card)] text-[var(--text-sub)] border-[var(--border)]'
                  )}
                >
                  {selectedCount > 0 ? `상품 ${selectedCount}개` : '선택 없음'}
                  <span className="ml-1 text-[10px]">{isExpanded ? '▲' : '▼'}</span>
                </button>

                {/* 저장 */}
                <button
                  onClick={() => saveRcUserSettings(user.id)}
                  disabled={isSaving || saving}
                  className={cn(
                    'px-3 py-[4px] rounded-lg border-none text-[11px] font-bold transition-colors',
                    isSaving || saving
                      ? 'bg-[var(--bg-subtle)] text-[var(--text-muted)] cursor-not-allowed'
                      : 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-accent-600 dark:hover:bg-accent-500 cursor-pointer'
                  )}
                >
                  {isSaving ? '저장 중...' : '저장'}
                </button>
              </div>

              {/* 상품 선택 패널 (펼침 시) */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-[var(--border)] bg-[var(--bg-card)]">
                  <div className="text-[10px] text-[var(--text-muted)] mb-2">
                    선택한 상품이 포함된 계약만 표시됩니다. 아무것도 선택 안 하면 전체 표시.
                  </div>
                  <div className="flex flex-col gap-2">
                    {categories.map((cat) => (
                      <div key={cat}>
                        {/* 카테고리명 */}
                        <div className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-wider mb-1">
                          {cat}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {grouped[cat].map((tt) => {
                            const checked = uSettings.ticketTypeIds?.has(tt.id);
                            return (
                              <button
                                key={tt.id}
                                type="button"
                                onClick={() => toggleRcUserTicketType(user.id, tt.id)}
                                className={cn(
                                  'px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors',
                                  checked
                                    ? 'bg-accent-50 text-accent-600 border-accent-300 dark:bg-pine-800 dark:text-accent-300'
                                    : 'bg-[var(--bg-subtle)] text-[var(--text-sub)] border-[var(--border)] hover:border-accent-400'
                                )}
                              >
                                {checked ? '✓ ' : ''}{tt.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

      </div>
    </section>
  );
}
